<script setup>


import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";
import { filterByNormalizedText } from "@/utils/textSearch";

// Services
import MaterialService from '@/api/services/material.service';
import MaterialTypeService from '@/api/services/materialType.service';
import EmployeeService from '@/api/services/employee.service';
import materialAssignmentService from '@/api/services/materialAssignment.service';

const optionsStatus = ref([
    {
        name: "Disponible",
        value: "available"
    },
    {
        name: "Asignado",
        value: "assigned"
    },
    {
        name: "Retirado",
        value: "decommissioned"
    },
]);

const uiStore = useUiStore();
const optionsTypes = ref([]);
const employees = ref([]);
const employeeSearchQuery = ref('');
const assignmentEmployee = ref(null);
const assignmentAssignedAt = ref(null);
const ownerTypes = ref([
    {
        name: "Sistemas",
        value: "systems"
    },
    {
        name: "Empleado",
        value: "employee"
    },
]);

const acquisitionTypes = ref([
    {
        name: "Compra",
        value: "purchased"
    },
    {
        name: "Renting",
        value: "renting"
    }
])

const filteredStatusOptions = computed(() => {
    const current = materialNew.value.status;

    return optionsStatus.value.filter(item => {
        // Si es assigned Y es el valor actual → se deja para que se vea en label,
        // pero NO se debe permitir seleccionarlo si se va a cambiar
        if (item.value === "assigned" && current !== "assigned") {
            return false;
        }

        return true;
    });
});

const materialRentingNear = computed(() => {
    if (materialNew.value && materialNew.value.acquisitionDate) {
        if (getRentingStatus(materialNew.value) === 'Renting < 2 meses') {
            return true;
        }
    }
    return false;
})
const materialRentingEnd = computed(() => {
    if (materialNew.value && materialNew.value.acquisitionDate) {
        if (getRentingStatus(materialNew.value) === 'Renting finalizado') {
            return true;
        }
    }

    return false;
})

const showEmployeeAssignmentFields = computed(() => {
    return !createMaterial.value
        && materialNew.value.status === 'available'
        && materialNew.value.ownerType === 'employee';
})
const filteredEmployees = computed(() => {
    return filterByNormalizedText(employees.value, employeeSearchQuery.value, employee => employee.fullName);
})


// eslint-disable-next-line no-undef
const emit = defineEmits(['closeDialog', 'duplicateMaterial'])

const materialNew = ref({
    "brand": '',
    "model": '',
    "serialTag": '',
    "internalCode": '',
    "notes": '',
    "type": null,
    "status": 'available',
    "name": '',
    "technicalSpecs": {
    },
    'ownerType': 'systems',
})

const createMaterial = ref(true);

// eslint-disable-next-line no-undef
const props = defineProps({
    material: Object,
})

const selectedMaterial = computed(() => {
    if (props && props.material) {
        return props.material;
    } else {
        return null;
    }
})

onMounted(() => {
    getMaterialsTypes();
    getEmployees();
    assignmentAssignedAt.value = formatDateTimeForPicker();
    if (selectedMaterial.value) {
        materialNew.value = { ...selectedMaterial.value };
        // El backend serializa un array PHP vacío como `[]` (no `{}`), así que un material
        // sin technicalSpecs llega aquí como array JS. Escribir claves sobre un array no se
        // refleja en JSON.stringify, así que hay que normalizarlo a objeto plano.
        if (!materialNew.value.technicalSpecs || Array.isArray(materialNew.value.technicalSpecs)) {
            materialNew.value.technicalSpecs = {};
        }
        if (!materialNew.value.ownerType) {
            materialNew.value.ownerType = '';
        }
        createMaterial.value = false;
    } else {
        createMaterial.value = true;
    }
})

const closeDialog = (changed = false) => {
    emit('closeDialog', changed)
}

const duplicateMaterial = () => {
    emit('duplicateMaterial', selectedMaterial.value)
}

// Normaliza variantes de "sin número de serie" (n/a, na, s/n, sn — sin importar
// mayúsculas, espacios o barras) al valor canónico "N/A", igual que el backend.
const normalizeSerialTag = (serialTag) => {
    if (!serialTag) {
        return serialTag;
    }
    const stripped = serialTag.toLowerCase().replace(/[/\s-]/g, '');
    if (['na', 'sn'].includes(stripped)) {
        return 'N/A';
    }
    return serialTag.trim();
}

const saveMaterial = () => {
    materialNew.value.serialTag = normalizeSerialTag(materialNew.value.serialTag);

    if (showEmployeeAssignmentFields.value && assignmentEmployee.value && !assignmentAssignedAt.value) {
        notification.addNotification({
            type: 'error',
            message: 'Tienes que indicar la fecha de entrega',
            duration: 6000
        })
        return;
    }

    if (createMaterial.value) {
        createNewMaterial();
    } else {
        updateMaterial();
    }
}

const createNewMaterial = () => {
    const params = {
        "serialTag": materialNew.value.serialTag,
        "status": materialNew.value.status,
        "type": materialNew.value.type['@id'],
        "model": materialNew.value.model,
        "brand": materialNew.value.brand,
        "notes": materialNew.value.notes,
        "internalCode": materialNew.value.internalCode,
        "name": materialNew.value.name,
        "technicalSpecs": materialNew.value.technicalSpecs,
        "ownerType": materialNew.value.ownerType,
        "acquisitionType": materialNew.value.acquisitionType,
        "acquisitionDate": materialNew.value.acquisitionDate,
        "rentingEndDate": materialNew.value.rentingEndDate,
    }

    MaterialService.createMaterial(params)
        .then((res) => {
            if (res.status === 200 || res.status === 201) {
                notification.addNotification({
                    type: 'success',
                    message: 'Creado el material correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
        })
}

const updateMaterial = () => {

    const params = {
        "serialTag": materialNew.value.serialTag,
        "status": materialNew.value.status,
        "type": materialNew.value.type['@id'],
        "model": materialNew.value.model,
        "brand": materialNew.value.brand,
        "notes": materialNew.value.notes,
        "internalCode": materialNew.value.internalCode,
        "name": materialNew.value.name,
        "technicalSpecs": materialNew.value.technicalSpecs,
        "ownerType": materialNew.value.ownerType,
        "acquisitionType": materialNew.value.acquisitionType,
        "acquisitionDate": materialNew.value.acquisitionDate,
        "rentingEndDate": materialNew.value.rentingEndDate,
    }

    const id = selectedMaterial.value.id;
    MaterialService.updateMaterial(id, params)
        .then((res) => {
            if (res.status === 200) {
                if (showEmployeeAssignmentFields.value && assignmentEmployee.value) {
                    createMaterialAssignment();
                    return;
                }
                notification.addNotification({
                    type: 'success',
                    message: 'Se ha actualizado el material correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
        })
}

const createMaterialAssignment = () => {
    if (!assignmentEmployee.value) {
        return;
    }

    if (!assignmentAssignedAt.value) {
        notification.addNotification({
            type: 'error',
            message: 'Tienes que indicar la fecha de entrega',
            duration: 6000
        })
        return;
    }

    const params = {
        responsible: 'employee',
        assignedAt: assignmentAssignedAt.value,
        employee: assignmentEmployee.value['@id'],
        material: selectedMaterial.value['@id'],
    }

    materialAssignmentService.createMaterial(params)
        .then((res) => {
            if (res && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    type: 'success',
                    message: 'Material guardado y añadido al empleado correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
        })
}

const getEmployees = (filters) => {
    const params = {
        filter: filters,
        active: true,
        'order[name]': 'asc',
        page: 1,
        itemsPerPage: 999999,
    };
    uiStore.startLoading();
    EmployeeService.getEmployees(params)
        .then((res) => {
            employees.value = res.data['hydra:member'];
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}
const filterEmployeesByName = (query) => {
    employeeSearchQuery.value = query;
}

const getMaterialsTypes = () => {
    // eslint-disable-next-line no-unused-vars
    const param = {
        'page': 1,
        'itemsPerPage': 99999,
    }
    uiStore.startLoading();
    MaterialTypeService.getMaterialTypes(param)
        .then((res) => {
            if (res.status === 200) {
                optionsTypes.value = res.data['hydra:member']
            }

            uiStore.stopLoading();
        })
}

const pad = (number) => String(number).padStart(2, '0');
const formatDateTimeForPicker = (date = new Date()) => {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const getRentingStatus = (material) => {
    if (!material?.rentingEndDate) return "";

    const end = new Date(material.rentingEndDate);
    const todaySameHour = new Date();
    todaySameHour.setHours(
        end.getHours(),
        end.getMinutes(),
        end.getSeconds(),
        end.getMilliseconds()
    );

    const diffMs = end - todaySameHour;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays < 0) {
        return "Renting finalizado";
    }

    if (diffDays <= 60) {
        return "Renting < 2 meses";
    }

    return "Renting OK";
}

const getMessageRenting = () => {

    if (materialNew.value && !materialNew.value.rentingAlertDisabled) {
        if (materialNew.value.rentingAlert2MonthsNotifiedAt) {
            return "El renting del material finalizará en menos de 2 meses";
        }
        if (materialNew.value.rentingAlert1MonthNotifiedAt) {
            return "El renting del material finalizará en menos de 1 mes";
        }
        if (materialNew.value.rentingAlert7DaysNotifiedAt) {
            return "El renting del material finalizará en menos de 7 días";
        }
    }
    return "El material está a punto de finalizar su renting."

}

</script>

<template>
    <div>
        <div class="header">
            <img style="width: 30px; margin-bottom: 14px;" src="@/assets/icons/box-blue.svg">
            <span class="header-title pl-4">
                {{ createMaterial ? 'Crear' : 'Modificar' }} Material
            </span>
        </div>
        <div class="row f-flex">
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="serialNumber" class="w-100">Número de serie</label>
                <el-input id="serialNumber" v-model="materialNew.serialTag"
                    placeholder="Introduce el número de serie (N/A si no dispone)" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="ownerType" class="w-100">Tipo de dueño</label>
                <el-select popper-class="material-modal-select" id="ownerType" v-model="materialNew.ownerType" filterable clearable placeholder="TODOS">
                    <el-option v-for="item in ownerTypes" :key="item.value" :label="item.name" :value="item.value" />
                </el-select>
            </div>
            <div class="col-6 el-col-xs-24 mt-3" v-if="showEmployeeAssignmentFields">
                <label for="materialEmployee" class="w-100">Empleado al que se entrega</label>
                <el-select popper-class="material-modal-select" id="materialEmployee" v-model="assignmentEmployee" filterable
                    :filter-method="filterEmployeesByName"
                    placeholder="Seleccione un empleado" :value-key="'@id'">
                    <el-option v-for="employee in filteredEmployees" :key="employee.id" :label="employee.fullName"
                        :value="employee" />
                </el-select>
            </div>
            <div class="col-6 el-col-xs-24 mt-3" v-if="showEmployeeAssignmentFields">
                <label for="materialAssignedAt" class="w-100">Fecha de entrega</label>
                <el-date-picker popper-class="material-modal-picker" id="materialAssignedAt" v-model="assignmentAssignedAt" name="assigned-at"
                    type="datetime" class="w-100" placeholder="Fecha entrega" format="DD/MM/YYYY HH:mm"
                    value-format="YYYY-MM-DD HH:mm" :picker-options="{ firstDayOfWeek: 1 }" />
            </div>
            <div class="col-12 el-col-xs-24 mt-3"
                v-if="materialNew.currentAssignment && materialNew.currentAssignment.employee && materialNew.currentAssignment.employee.fullName">
                <label for="assignedTo" class="w-100">Asignado A</label>
                <el-input id="assignedTo" v-model="materialNew.currentAssignment.employee.fullName" disabled
                    placeholder="Introduce el número de serie" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="brand" class="w-100">Marca</label>
                <el-input id="brand" v-model="materialNew.brand" placeholder="Introduce la marca" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="model" class="w-100">Modelo</label>
                <el-input id="model" v-model="materialNew.model" placeholder="Introduce el modelo" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="name" class="w-100">Nombre</label>
                <el-input id="name" v-model="materialNew.name" placeholder="Introduce el nombre" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="internalCode" class="w-100">Código interno</label>
                <el-input id="internalCode" v-model="materialNew.internalCode"
                    placeholder="Introduce el número de serie" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="materialAcquisitionType" class="w-100">Tipo de adquisición</label>
                <el-select popper-class="material-modal-select" id="materialAcquisitionType" v-model="materialNew.acquisitionType" filterable clearable
                    placeholder="TODOS" :value-key="'@id'">
                    <el-option v-for="item in acquisitionTypes" :key="item.value" :label="item.name"
                        :value="item.value" />
                </el-select>
            </div>
            <div class="col-6 el-col-xs-24 mt-3" v-if="materialNew && materialNew.acquisitionType">
                <label for="fpurchased" class="w-100">{{ materialNew.acquisitionType === 'renting' ?
                    'Fecha de inicio del renting' : 'Fecha de la compra' }}</label>
                <el-date-picker popper-class="material-modal-picker" id="fpurchased" v-model="materialNew.acquisitionDate" name="purchased-date'" type="date"
                    class="w-100"
                    :placeholder="materialNew.acquisitionType === 'renting' ? 'Fecha de inicio del renting' : 'Fecha de la compra'"
                    format="DD/MM/YYYY" value-format="YYYY-MM-DD" :picker-options="{ firstDayOfWeek: 1 }" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3"
                v-if="materialNew && materialNew.acquisitionType && materialNew.acquisitionType === 'renting'">
                <label for="frenting" class="w-100">Fecha de fin de renting</label>
                <el-date-picker popper-class="material-modal-picker" id="frenting" v-model="materialNew.rentingEndDate" name="renting-date'" type="date"
                    class="w-100" placeholder="Fecha de fin del renting" format="DD/MM/YYYY" value-format="YYYY-MM-DD"
                    :picker-options="{ firstDayOfWeek: 1 }" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3"
                v-if="materialNew && materialNew.acquisitionType && materialNew.acquisitionType === 'renting'">
                <label for="frenting" class="w-100">Deshabilitar alertas renting</label>
                <el-switch v-model="materialNew.rentingAlertDisabled" active-text="Sí" inactive-text="No" />
            </div>
            <div class="col-md-12 el-col-xs-24 mt-4" v-if="materialRentingNear && !materialNew.rentingAlertDisabled">
                <div class="alert-custom warning-adBlue">
                    <el-icon style="color: #faad14; font-size: 24px; margin-right: 8px; margin-top: -3px;">
                        <WarnTriangleFilled />
                    </el-icon>
                    <span style="font-weight: 600; font-size: 15px;">
                        {{ getMessageRenting() }}
                    </span>
                </div>
            </div>
            <div class="col-md-12 el-col-xs-24 mt-4" v-if="materialRentingEnd">
                <div class="alert-custom wrong-adBlue">
                    <el-icon style="color: #e53935; font-size: 24px; margin-right: 8px; margin-top: -3px;">
                        <WarnTriangleFilled />
                    </el-icon>
                    <span style="font-weight: 600; font-size: 15px;">
                        El material tiene el renting finalizado.
                    </span>
                </div>
            </div>
            <div class="col-6 el-col-xs-24 mt-3" v-if="!createMaterial">
                <label for="materialStatus" class="w-100">Estado de material</label>
                <el-select popper-class="material-modal-select" id="materialStatus" v-model="materialNew.status" filterable clearable placeholder="TODOS"
                    :disabled="materialNew.status === 'assigned' || materialNew.status === 'decommissioned'">
                    <el-option v-for="item in filteredStatusOptions" :key="item.value" :label="item.name"
                        :value="item.value" />
                </el-select>
            </div>
            <div class="col-6 el-col-xs-24 mt-3" v-if="materialNew && materialNew.decommissionedAt">
                <label for="frenting" class="w-100">Fecha de baja</label>
                <el-date-picker popper-class="material-modal-picker" id="frenting" v-model="materialNew.decommissionedAt" name="renting-date'" type="date"
                    class="w-100" placeholder="Fecha de renting" format="DD/MM/YYYY" value-format="YYYY-MM-DD" disabled
                    :picker-options="{ firstDayOfWeek: 1 }" />
            </div>
            <div class="col-6 el-col-xs-24 mt-3" v-if="materialNew && materialNew.decommissionReason">
                <label class="w-100">Razón de baja:</label>
                <el-input v-model="materialNew.decommissionReason" placeholder="Motivo de baja" type="textarea" rows="3"
                    disabled />
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="materialType" class="w-100">Tipo de material</label>
                <el-select popper-class="material-modal-select" id="materialType" v-model="materialNew.type" filterable clearable placeholder="TODOS"
                    :value-key="'@id'">
                    <el-option v-for="item in optionsTypes" :key="item['@id']" :label="item.name" :value="item" />
                </el-select>
            </div>
            <div class="col-12" v-if="materialNew.type && materialNew.type.name && materialNew.type.name == 'Portátil'">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="antivirus" class="w-100">Antivirus</label>
                        <el-input id="antivirus" v-model="materialNew.technicalSpecs.antivirus"
                            placeholder="Introduce el antivirus del portatil" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="processor" class="w-100">Procesador</label>
                        <el-input v-model="materialNew.technicalSpecs.processor" id="processor"
                            placeholder="Introduce el procesador del portatil" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="memory" class="w-100">Memoria</label>
                        <el-input v-model="materialNew.technicalSpecs.memory" id="memory"
                            placeholder="Introduce la memoria del portatil" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="storage" class="w-100">Almacenamiento</label>
                        <el-input v-model="materialNew.technicalSpecs.storage" id="storage"
                            placeholder="Introduce el almacenamiento del portatil" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="os" class="w-100">Sistema Operativo</label>
                        <el-input v-model="materialNew.technicalSpecs.os" id="os"
                            placeholder="Introduce el Sistema Operativo del portatil" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="mac" class="w-100">MAC</label>
                        <el-input v-model="materialNew.technicalSpecs.mac" id="mac"
                            placeholder="Introduce el MAC del portatil" />
                    </div>
                </div>
            </div>
            <div class="col-12" v-if="materialNew.type && materialNew.type.name && materialNew.type.name == 'PC'">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="antivirusPc" class="w-100">Antivirus</label>
                        <el-input id="antivirusPc" v-model="materialNew.technicalSpecs.antivirus"
                            placeholder="Introduce el antivirus del PC" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="processorPc" class="w-100">Procesador</label>
                        <el-input v-model="materialNew.technicalSpecs.processor" id="processorPc"
                            placeholder="Introduce el procesador del PC" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="memoryPc" class="w-100">Memoria</label>
                        <el-input v-model="materialNew.technicalSpecs.memory" id="memoryPc"
                            placeholder="Introduce la memoria del PC" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="storagePc" class="w-100">Almacenamiento</label>
                        <el-input v-model="materialNew.technicalSpecs.storage" id="storagePc"
                            placeholder="Introduce el almacenamiento del PC" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="osPc" class="w-100">Sistema Operativo</label>
                        <el-input v-model="materialNew.technicalSpecs.os" id="osPc"
                            placeholder="Introduce el Sistema Operativo del PC" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="macPc" class="w-100">MAC</label>
                        <el-input v-model="materialNew.technicalSpecs.mac" id="macPc"
                            placeholder="Introduce el MAC del PC" />
                    </div>
                </div>
            </div>
            <div class="col-12" v-if="materialNew.type && materialNew.type.name && materialNew.type.name == 'Monitor'">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="resolution" class="w-100">Resolución</label>
                        <el-input v-model="materialNew.technicalSpecs.resolution" id="resolution"
                            placeholder="Introduce la resolución del Monitor" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="size" class="w-100">Tamaño</label>
                        <el-input v-model="materialNew.technicalSpecs.size" id="size"
                            placeholder="Introduce el tamaño del Monitor" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="connection" class="w-100">Conexión</label>
                        <el-input v-model="materialNew.technicalSpecs.connection" id="connection"
                            placeholder="Introduce la conexión del Monitor" />
                    </div>
                </div>
            </div>
            <div class="col-12"
                v-if="materialNew.type && materialNew.type.name && materialNew.type.name == 'Docking Station'">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="mac" class="w-100">MAC</label>
                        <el-input v-model="materialNew.technicalSpecs.mac" id="mac"
                            placeholder="Introduce el MAC del Docking station" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="ports" class="w-100">Puertos</label>
                        <el-input v-model="materialNew.technicalSpecs.ports" id="ports"
                            placeholder="Introduce los puertos del Docking station" />
                    </div>
                </div>
            </div>
            <div class="col-12" v-if="materialNew.type && materialNew.type.name && materialNew.type.name == 'Switch'">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="mac" class="w-100">MAC</label>
                        <el-input v-model="materialNew.technicalSpecs.mac" id="mac"
                            placeholder="Introduce el MAC del Switch" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="ports" class="w-100">Puertos</label>
                        <el-input v-model="materialNew.technicalSpecs.ports" id="ports"
                            placeholder="Introduce los puertos del Switch" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="speed" class="w-100">Velocidad</label>
                        <el-input v-model="materialNew.technicalSpecs.speed" id="speed"
                            placeholder="Introduce la velocidad del Switch" />
                    </div>
                    <div class="col-6 el-col-xs-24 mt-3">
                        <label for="firmware" class="w-100">Firmware</label>
                        <el-input v-model="materialNew.technicalSpecs.firmware" id="firmware"
                            placeholder="Introduce el firmware del Switch" />
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3">
                <label for="notes" class="w-100">Notas</label>
                <el-input id="notes" type="textarea" v-model="materialNew.notes" placeholder="Escribe tus comentarios"
                    :rows="5" class="custom-textarea" />
            </div>
        </div>
        <div class="material-dialog__actions" :class="{ 'material-dialog__actions--create': createMaterial }">
            <button v-if="!createMaterial" type="button" class="btn btn_blue material-dialog__action"
                @click="duplicateMaterial">
                Duplicar
            </button>
            <div class="material-dialog__primary-actions">
                <button class="btn btn_yellow material-dialog__action" @click="closeDialog">
                    Cancelar
                </button>
                <button type="submit" class="btn btn_red material-dialog__action" @click="saveMaterial">
                    Guardar
                </button>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-title {
    font-size: 30px;
    margin-left: 5px;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #474ce6;
}

.custom-textarea:deep(.el-textarea__inner) {
    font-size: 14px !important;
    border-radius: 20px;
}

.material-dialog__actions {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    margin-top: 24px;
}

.material-dialog__actions--create {
    justify-content: flex-end;
}

.material-dialog__primary-actions {
    display: grid;
    grid-template-columns: repeat(2, 150px);
    gap: 20px;
    flex: 0 0 320px;
    margin-right: 16px;
}

.material-dialog__action {
    box-sizing: border-box;
    min-width: 0;
    width: 100%;
    white-space: nowrap;
}

.material-dialog__actions>.material-dialog__action {
    flex: 0 0 150px;
    width: 150px;
}

@media (max-width: 767.98px) {
    .material-dialog__actions {
        flex-direction: column;
        padding-right: 12px;
    }

    .material-dialog__primary-actions {
        grid-template-columns: 1fr;
        flex: initial;
        margin-right: 0;
        width: 100%;
    }

    .material-dialog__actions>.material-dialog__action,
    .material-dialog__action {
        flex: 0 0 auto;
        width: 100%;
    }
}

.wrong-adBlue,
.warning-adBlue {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 6px;
}

.wrong-adBlue {
    background-color: #fdecea;
    border-left: 5px solid #e53935;
}

.warning-adBlue {
    background-color: #fffbe8;
    border-left: 5px solid #faad14;
}
</style>
